import { EpicDeliveryStatus } from './EpicDeliveryStatus';
import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  boardHref,
  categories,
  counts,
  deliveryItems,
  descendants,
  epicActivityRank,
  epicPullRequests,
  milestoneFeatures,
  featureMatches,
  labels,
  matches,
  percentage,
  routeEpic,
  showEpicInPortfolio,
} from "./model";
import type { BoardItem, Category, Snapshot } from "./model";
import "./boards.css";
import { PullRequests } from "./PullRequests";

async function loadBoard(): Promise<Snapshot> {
  const response = await fetch("/api/boards", {
    signal: AbortSignal.timeout(95_000),
  });
  if (response.status === 401)
    throw new Error("Your session has expired. Sign in to view the boards.");
  if (!response.ok)
    throw new Error(
      "We couldn’t refresh progress from Azure DevOps. Your last successful snapshot stays visible below.",
    );
  return response.json() as Promise<Snapshot>;
}

function Arrow({ back = false }: { back?: boolean }) {
  return <span aria-hidden="true">{back ? "←" : "↗"}</span>;
}
function Badge({ item }: { item: BoardItem }) {
  return (
    <span className={`board-badge ${item.blocked ? "blocked" : item.category}`}>
      {item.blocked ? "Blocked" : item.state}
    </span>
  );
}
function Bar({ items }: { items: BoardItem[] }) {
  const c = counts(items);
  return (
    <div
      className="board-bar"
      role="img"
      aria-label={categories.map((k) => `${c[k]} ${labels[k]}`).join(", ")}
    >
      {categories.map(
        (k) =>
          c[k] > 0 && (
            <span
              key={k}
              className={k}
              style={{ width: `${(c[k] / c.total) * 100}%` }}
              title={`${labels[k]}: ${c[k]}`}
            />
          ),
      )}
    </div>
  );
}
function Legend() {
  return (
    <div className="board-legend">
      {categories.map((k) => (
        <span key={k}>
          <i className={k} />
          {labels[k]}
        </span>
      ))}
    </div>
  );
}
function Summary({
  items,
  title,
  onFilter,
}: {
  items: BoardItem[];
  title: string;
  onFilter: (category: Category) => void;
}) {
  const c = counts(items);
  let offset = 0;
  const segments = categories.map((k) => {
    const start = offset;
    offset += c.total ? (c[k] / c.total) * 100 : 0;
    return `var(--${k}) ${start}% ${offset}%`;
  });
  return (
    <section className="board-summary" aria-label="Progress summary">
      <div className="board-summary-main">
        <div className="board-eyebrow">{title}</div>
        <div className="board-summary-heading">
          <strong>{c.total.toLocaleString("en-GB")}</strong>
          <span>scoped work items</span>
        </div>
        <Bar items={items} />
        <Legend />
        <p className="board-note">
          Completion counts Resolved + Closed work items, not effort.
          PR, CI and deployment status are tracked separately.
        </p>
      </div>
      <div className="board-ring-wrap">
        <div
          className="board-ring"
          style={
            {
              "--ring": c.total
                ? `conic-gradient(${segments.join(",")})`
                : "var(--planned)",
            } as CSSProperties
          }
          role="img"
          aria-label={`${percentage(c.complete, c.total)} percent of scoped work items complete`}
        >
          <div>
            <strong>
              {c.total ? `${percentage(c.complete, c.total)}%` : "—"}
            </strong>
            <span>complete</span>
          </div>
        </div>
      </div>
      <div className="board-stat-grid">
        {(["closed", "resolved", "active", "planned"] as const).map((k) => (
          <button
            key={k}
            className={`board-stat ${k}`}
            onClick={() => {
              onFilter(k);
            }}
            title={`Show ${labels[k].toLowerCase()} work`}
          >
            <span>
              <i />
              {labels[k]}
            </span>
            <strong>{c[k]}</strong>
            <Arrow />
          </button>
        ))}
      </div>
    </section>
  );
}

function EpicCard({
  epic,
  items,
  navigate,
}: {
  epic: BoardItem;
  items: BoardItem[];
  navigate: (id?: number) => void;
}) {
  const c = counts(items);
  const features = items.filter((x) => x.type === "Feature");
  return (
    <a
      className="board-epic-card"
      href={boardHref(epic.id)}
      onClick={(e) => {
        if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
          e.preventDefault();
          navigate(epic.id);
        }
      }}
    >
      <div className="board-card-top">
        <span className="board-id">EPIC {epic.id}</span>
        <Badge item={epic} />
      </div>
      <h3>{epic.title}</h3>
      <p className="board-card-description">
        {epic.description ||
          "Open this epic to explore its scope, features and delivery progress."}
      </p>
      <div className="board-card-progress">
        <strong>
          {c.total ? `${percentage(c.complete, c.total)}%` : "—"}
          <small>complete</small>
        </strong>
        <span>
          {c.complete} / {c.total} items
        </span>
      </div>
      <Bar items={items} />
      <div className="board-card-counts">
        <span>
          <i className="active" />
          {c.active} in progress
        </span>
        <span>
          <i className="resolved" />
          {c.resolved} resolved
        </span>
        {c.blocked > 0 && (
          <span className="board-risk">{c.blocked} blocked</span>
        )}
      </div>
      <EpicDeliveryStatus items={[epic, ...items]} />
      <footer>
        <span>
          {features.length} milestones <b>·</b> {epicPullRequests([epic, ...items]).length} linked PRs <b>·</b> {epic.owner || "Unassigned"}
        </span>
        <Arrow />
      </footer>
    </a>
  );
}

function WorkTable({
  items,
  query,
  category,
}: {
  items: BoardItem[];
  query: string;
  category: string;
}) {
  const visible = items.filter(
    (x) =>
      !["Epic", "Feature"].includes(x.type) &&
      matches(x, query) &&
      (category === "all" ||
        (category === "blocked" ? x.blocked : x.category === category)),
  );
  const [limit, setLimit] = useState(50);
  if (!visible.length)
    return <p className="board-empty">No work items match these filters.</p>;
  return (
    <>
      <div className="board-table-scroll">
        <table className="board-work-table">
          <thead>
            <tr>
              <th>Work item</th>
              <th>Status</th>
              <th>Owner</th>
              <th>Changed</th>
              <th>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {visible.slice(0, limit).map((item) => (
              <tr key={item.id}>
                <td>
                  <a href={item.url} target="_blank" rel="noreferrer">
                    <span className="board-id">
                      {item.type} #{item.id}
                    </span>
                    <strong>{item.title}</strong>
                  </a>
                </td>
                <td>
                  <Badge item={item} />
                </td>
                <td>{item.owner || "Unassigned"}</td>
                <td>
                  {new Date(item.changedAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                  })}
                </td>
                <td>
                  {item.pullRequests.length ? (
                    item.pullRequests.map((link) => (
                      <a
                        className="board-evidence"
                        key={link.url}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {link.label} <Arrow />
                      </a>
                    ))
                  ) : (
                    <span className="board-muted">No linked PR</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {visible.length > limit && (
        <button
          className="board-button board-load"
          onClick={() => {
            setLimit(limit + 50);
          }}
        >
          Show 50 more ({visible.length - limit} remaining)
        </button>
      )}
    </>
  );
}

function Milestones({ items, query, category }: { items: BoardItem[]; query: string; category: string }) {
  const features = milestoneFeatures(items);
  const visible = features.map((feature) => ({ feature, children: descendants(feature.id, items) }))
    .filter(({ feature, children }) => featureMatches(feature, children, query, category));
  return <section aria-label="Epic milestones" className="board-milestones">
    <div><h2>Milestones</h2><p className="board-note">Features in target-date order, then undated features. Completion counts Resolved + Closed work items; their statuses remain separate. Dates are targets, not dependencies.</p></div>
    <Legend />
    {!features.length ? <p className="board-empty">No milestones yet. Add Features beneath this epic in Azure DevOps to define them.</p>
      : !visible.length ? <p className="board-empty">No milestones match these filters.</p> : null}
    <ol className="board-milestone-list">
      {visible.map(({ feature, children }) => {
        const c = counts(children);
        return <li key={feature.id}>
          <details className="board-milestone">
            <summary>
              <div className="board-milestone-top"><span className="board-id">FEATURE {feature.id}</span><Badge item={feature} /></div>
              <h3>{feature.title}</h3>
              <div className="board-milestone-meta"><span>{feature.targetDate ? `Target ${new Date(feature.targetDate).toLocaleDateString("en-GB", { timeZone: "UTC", day: "numeric", month: "short", year: "numeric" })}` : "No target date"}</span><span>{feature.owner || "Unassigned"}</span></div>
              <Bar items={children} />
              <div className="board-milestone-meta"><strong>{c.total ? `${percentage(c.complete, c.total)}% complete · ${c.complete}/${c.total} items` : "Scope not broken down"}</strong><span>{c.resolved} resolved · {c.active} in progress</span></div>
              {(c.blocked > 0 || feature.blocked) && <p className="board-risk">{feature.blocked ? "Milestone blocked. " : ""}{c.blocked > 0 ? `${c.blocked} blocked work items` : ""}</p>}
              <span className="board-note">Expand to view work and PR evidence ↓</span>
            </summary>
            <div className="board-feature-body"><a className="board-evidence" href={feature.url} target="_blank" rel="noreferrer">Milestone in Azure DevOps ↗</a>
              <WorkTable items={children} query={matches(feature, query) ? "" : query} category={category} />
            </div>
          </details>
        </li>;
      })}
    </ol>
    <p className="board-note">Work outside Features remains in All work and the epic totals.</p>
  </section>;
}

function EpicDetail({
  epic,
  items,
  query,
  category,
}: {
  epic: BoardItem;
  items: BoardItem[];
  query: string;
  category: string;
}) {
  const [tab, setTab] = useState("milestones");
  const features = items.filter((x) => x.type === "Feature");
  const featureDescendants = useMemo(
    () => new Map(features.map((f) => [f.id, descendants(f.id, items)])),
    [features, items],
  );
  const grouped = new Set(
    [...featureDescendants.values()].flat().map((x) => x.id),
  );
  const directItems = items.filter(
    (x) => !grouped.has(x.id) && !["Epic", "Feature"].includes(x.type),
  );
  const visibleFeatures = features
    .map((feature, index) => ({
      feature,
      index,
      children: featureDescendants.get(feature.id) ?? [],
    }))
    .filter(({ feature, children }) =>
      featureMatches(feature, children, query, category),
    );
  return (
    <>
      <div className="board-tabs" role="tablist" aria-label="Epic views">
        {[
          ["milestones", `Milestones · ${features.length}`],
          ["features", `Features · ${features.length}`],
          ["prs", `PRs & pipelines · ${epicPullRequests([epic, ...items]).length}`],
          [
            "work",
            `All work · ${items.filter((x) => !["Epic", "Feature"].includes(x.type)).length}`,
          ],
          ["brief", "Epic brief"],
        ].map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => {
              setTab(key ?? "features");
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {tab === "milestones" ? (
          <Milestones items={items} query={query} category={category} />
        ) : tab === "prs" ? (
          <PullRequests items={[epic, ...items]} />
        ) : tab === "brief" ? (
          <section className="board-brief">
            <h2>What we’re building</h2>
            <p>
              {epic.description ||
                "No brief has been recorded on this epic yet."}
            </p>
            <a href={epic.url} target="_blank" rel="noreferrer">
              Read the complete epic and discussion in Azure DevOps <Arrow />
            </a>
            <p className="board-note">
              Scope, decisions and deferred work belong on the epic and its
              linked specifications. Updates here follow Azure DevOps
              automatically.
            </p>
          </section>
        ) : tab === "work" ? (
          <WorkTable items={items} query={query} category={category} />
        ) : (
          <div className="board-features">
            {features.length > 0 && visibleFeatures.length === 0 && (
              <p className="board-empty">No features match these filters.</p>
            )}
            {visibleFeatures.map(({ feature, index, children }) => {
              const c = counts(children);
              return (
                <details
                  key={feature.id}
                  className="board-feature"
                  open={query || category !== "all" ? true : undefined}
                >
                  <summary>
                    <span className="board-feature-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div className="board-feature-name">
                      <span className="board-id">FEATURE {feature.id}</span>
                      <h3>{feature.title}</h3>
                    </div>
                    <Badge item={feature} />
                    <div className="board-feature-progress">
                      <span>
                        {c.total
                          ? `${percentage(c.complete, c.total)}% complete`
                          : "Scope not broken down"}
                      </span>
                      <Bar items={children} />
                    </div>
                    <span className="board-chevron" aria-hidden="true">
                      ⌄
                    </span>
                  </summary>
                  <div className="board-feature-body">
                    {feature.description && (
                      <p className="board-feature-description">
                        {feature.description}
                      </p>
                    )}
                    <div className="board-feature-links">
                      <a href={feature.url} target="_blank" rel="noreferrer">
                        Feature in Azure DevOps <Arrow />
                      </a>
                      <span>
                        {c.total} scoped items · {c.resolved} resolved ·{" "}
                        {c.active} in progress
                      </span>
                    </div>
                    <WorkTable
                      items={children}
                      query={matches(feature, query) ? "" : query}
                      category={category}
                    />
                  </div>
                </details>
              );
            })}
            {directItems.length > 0 && (
              <section className="board-direct">
                <h3>Work directly under this epic</h3>
                <WorkTable
                  items={directItems}
                  query={query}
                  category={category}
                />
              </section>
            )}
            {!features.length && !directItems.length && (
              <p className="board-empty">
                This epic has no visible child work yet. Add features or stories
                in Azure DevOps and they will appear here.
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export function Boards() {
  const [epicId, setEpicId] = useState(() =>
    routeEpic(window.location.pathname),
  );
  const [search, setSearch] = useState("");
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  const [category, setCategory] = useState("all");
  const [presenting, setPresenting] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  const [clock, setClock] = useState(Date.now());
  const board = useQuery({
    queryKey: ["delivery-boards"],
    queryFn: loadBoard,
    refetchInterval: 60_000,
    staleTime: 50_000,
    retry: 1,
  });
  useEffect(() => {
    const onPop = () => {
      setEpicId(routeEpic(window.location.pathname));
      setSearch("");
      setCategory("all");
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPresenting(false);
    };
    window.addEventListener("popstate", onPop);
    window.addEventListener("keydown", onKey);
    const timer = window.setInterval(() => {
      setClock(Date.now());
    }, 15_000);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("keydown", onKey);
      window.clearInterval(timer);
    };
  }, []);
  const items = useMemo(() => board.data?.items ?? [], [board.data]);
  const epics = useMemo(
    () =>
      items
        .filter((x) => x.type === "Epic" && x.category !== "removed")
        .sort((a, b) => {
          const rank = (x: BoardItem) =>
            x.category === "active"
              ? 0
              : x.category === "resolved"
                ? 1
                : x.category === "planned"
                  ? 2
                  : 3;
          return (
            rank(a) - rank(b) ||
            new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
          );
        }),
    [items],
  );
  const epicItems = useMemo(
    () => new Map(epics.map((epic) => [epic.id, descendants(epic.id, items)])),
    [epics, items],
  );
  const epic = epics.find((x) => x.id === epicId);
  useEffect(() => {
    document.title = epic
      ? `${epic.title} · Marque Boards`
      : "Delivery portfolio · Marque Boards";
  }, [epic]);
  function navigate(id?: number) {
    window.history.pushState({}, "", boardHref(id));
    setEpicId(id);
    setSearch("");
    setCategory("all");
    window.scrollTo(0, 0);
  }
  const scopedEpics = epics
    .filter((x) => showEpicInPortfolio(x, showAllCompleted, clock))
    .sort(
      (a, b) =>
        epicActivityRank(a, epicItems.get(a.id) ?? []) -
        epicActivityRank(b, epicItems.get(b.id) ?? []),
    );
  const visibleEpics = scopedEpics.filter(
    (x) =>
      (matches(x, search) ||
        (epicItems.get(x.id) ?? []).some((child) => matches(child, search))) &&
      (category === "all" ||
        (epicItems.get(x.id) ?? []).some((child) =>
          category === "blocked" ? child.blocked : child.category === category,
        )),
  );
  const scopedItems = epicId
    ? (epicItems.get(epicId) ?? [])
    : [
        ...new Map(
          scopedEpics
            .flatMap((x) => epicItems.get(x.id) ?? [])
            .map((x) => [x.id, x]),
        ).values(),
      ];
  const stats = counts(scopedItems);
  const recent = [...scopedItems]
    .filter((x) => !["Epic", "Feature"].includes(x.type))
    .sort((a, b) => Date.parse(b.changedAt) - Date.parse(a.changedAt))
    .slice(0, 5);
  const stale =
    !!board.data && clock - Date.parse(board.data.fetchedAt) > 150_000;
  const parented = new Set(
    [...epicItems.values()].flat().map((item) => item.id),
  );
  const unparented = deliveryItems(items).filter(
    (item) => !parented.has(item.id),
  ).length;
  return (
    <div className={`boards ${presenting ? "board-presenting" : ""}`}>
      <header className="board-header">
        <a
          href="/boards"
          className="board-brand"
          onClick={(e) => {
            e.preventDefault();
            navigate();
          }}
        >
          <span className="board-mark" aria-hidden="true">
            M
          </span>
          <strong>
            marque<span> / delivery</span>
          </strong>
        </a>
        <nav aria-label="Board navigation">
          <a
            href="/boards"
            onClick={(e) => {
              e.preventDefault();
              navigate();
            }}
            aria-current={!epicId ? "page" : undefined}
          >
            Portfolio
          </a>
          <a href="/">
            Ops overview <Arrow />
          </a>
        </nav>
        <div className="board-header-actions">
          <span
            className={`board-live ${board.isError || stale ? "stale" : ""}`}
          >
            <i />
            {board.isError || stale
              ? "Refresh needed"
              : board.data
                ? "Live from Azure DevOps"
                : "Connecting"}
          </span>
          <button
            className="board-button"
            onClick={() => {
              setPresenting(!presenting);
            }}
          >
            {presenting ? "Exit presentation" : "Present"}
          </button>
        </div>
      </header>
      <main className="board-main">
        <div className="board-breadcrumb">
          {epicId ? (
            <button
              onClick={() => {
                navigate();
              }}
            >
              <Arrow back /> All epics
            </button>
          ) : (
            <span>PROJECT DELIVERY</span>
          )}
          <span>{epicId ? `EPIC ${epicId}` : "MARQUE"}</span>
        </div>
        <div className="board-title-row">
          <div>
            <h1>
              {epic?.title ??
                (epicId ? `Epic ${epicId}` : "The bigger picture.")}
            </h1>
            <p>
              {epic
                ? `${epic.owner || "Unassigned"} · ${epic.state}`
                : "Every epic. One view of what’s moving, what’s next, and what needs attention."}
            </p>
          </div>
          <div className="board-page-actions">
            <button
              className="board-button"
              disabled={board.isFetching}
              onClick={() => {
                void board.refetch();
              }}
            >
              {board.isFetching ? "Refreshing…" : "↻ Refresh"}
            </button>
            <button
              className="board-button"
              onClick={() => {
                void navigator.clipboard
                  .writeText(window.location.href)
                  .then(() => {
                    setShareMessage("Link copied");
                  })
                  .catch(() => {
                    setShareMessage(
                      "Copy the address from your browser to share this board.",
                    );
                  });
              }}
            >
              Copy link
            </button>
            <span role="status">{shareMessage}</span>
          </div>
        </div>
        {board.isError && (
          <div className="board-error" role="alert">
            {board.error.message}{" "}
            <a
              href={`/signin?returnUrl=${encodeURIComponent(window.location.pathname)}`}
            >
              Sign in
            </a>
            <button
              onClick={() => {
                void board.refetch();
              }}
            >
              Retry
            </button>
          </div>
        )}
        {stale && !board.isError && (
          <p className="board-error" role="status">
            This snapshot is more than two minutes old. Refresh before
            presenting current progress.
          </p>
        )}
        {board.isPending ? (
          <section className="board-loading" role="status">
            <span className="board-loading-dot" />
            <h2>Bringing the portfolio together</h2>
            <p>
              Reading the latest epic and work-item progress from Azure DevOps.
            </p>
          </section>
        ) : board.data && epicId && !epic ? (
          <section className="board-empty">
            <h2>Epic not available</h2>
            <p>
              This epic is removed, outside this project, or not visible to the
              board connection.
            </p>
            <button
              className="board-button"
              onClick={() => {
                navigate();
              }}
            >
              Back to portfolio
            </button>
          </section>
        ) : (
          board.data && (
            <>
              <Summary
                items={scopedItems}
                title={
                  epic
                    ? "EPIC DELIVERY"
                    : `${showAllCompleted ? "ALL" : "OPEN + RECENTLY COMPLETED"} EPICS · ${scopedEpics.length} IN VIEW`
                }
                onFilter={setCategory}
              />
              <div className="board-workspace">
                <section className="board-primary">
                  <div className="board-section-title">
                    <h2>{epic ? "Inside this epic" : "Explore the epics"}</h2>
                    {!epic && (
                      <span>
                        {visibleEpics.length} of {scopedEpics.length}
                      </span>
                    )}
                  </div>
                  <div className="board-controls">
                    <label className="board-search">
                      <span aria-hidden="true">⌕</span>
                      <input
                        aria-label="Search epics and work"
                        placeholder="Search by title, owner or ticket…"
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                        }}
                      />
                    </label>
                    {!epic && (
                      <label className="board-completed-toggle" title="By default, resolved and closed epics remain visible for 24 hours after their state changes.">
                        <input type="checkbox" checked={showAllCompleted} onChange={(e) => setShowAllCompleted(e.target.checked)} />
                        Show all resolved / closed epics
                      </label>
                    )}
                    <select
                      aria-label="Work status"
                      value={category}
                      onChange={(e) => {
                        setCategory(e.target.value);
                      }}
                    >
                      <option value="all">All work states</option>
                      {categories.map((k) => (
                        <option key={k} value={k}>
                          {labels[k]}
                        </option>
                      ))}
                      <option value="blocked">Blocked</option>
                    </select>
                  </div>
                  {category !== "all" && (
                    <div className="board-filter-note">
                      Showing epics or features containing{" "}
                      {category === "blocked"
                        ? "blocked"
                        : labels[category as Category].toLowerCase()}{" "}
                      work. Summary totals retain the full scope.
                      <button
                        onClick={() => {
                          setCategory("all");
                        }}
                      >
                        Clear filter ×
                      </button>
                    </div>
                  )}
                  {epic ? (
                    <EpicDetail
                      key={epic.id}
                      epic={epic}
                      items={scopedItems}
                      query={search}
                      category={category}
                    />
                  ) : (
                    <div className="board-epic-grid">
                      {visibleEpics.map((x) => (
                        <EpicCard
                          key={x.id}
                          epic={x}
                          items={epicItems.get(x.id) ?? []}
                          navigate={navigate}
                        />
                      ))}
                      {!visibleEpics.length && (
                        <p className="board-empty">
                          No epics match these filters.
                        </p>
                      )}
                    </div>
                  )}
                </section>
                <aside className="board-aside">
                  <section className="board-attention">
                    <div className="board-eyebrow">ATTENTION</div>
                    <h2>
                      {stats.blocked
                        ? `${stats.blocked} blocked items`
                        : "No flagged blockers"}
                    </h2>
                    <p>
                      {stats.blocked
                        ? "Work explicitly marked as blocked in Azure DevOps."
                        : "No scoped items carry a blocked flag. Unrecorded risks may still exist."}
                    </p>
                    {stats.blocked > 0 && (
                      <button
                        onClick={() => {
                          setCategory("blocked");
                        }}
                      >
                        Review blockers <Arrow />
                      </button>
                    )}
                  </section>
                  <section className="board-activity">
                    <div className="board-section-title">
                      <h2>Latest movement</h2>
                      <span>ADO updates</span>
                    </div>
                    {recent.map((x) => (
                      <a
                        key={x.id}
                        href={x.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <span className={`board-activity-dot ${x.category}`} />
                        <div>
                          <span className="board-id">
                            #{x.id} <b>·</b>{" "}
                            {new Date(x.changedAt).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                            })}
                          </span>
                          <strong>{x.title}</strong>
                          <span
                            className={`board-activity-state ${x.category}`}
                          >
                            {x.state}
                          </span>
                        </div>
                      </a>
                    ))}
                    {!recent.length && (
                      <p className="board-note">
                        No scoped work-item activity yet.
                      </p>
                    )}
                  </section>
                  <section className="board-method">
                    <h3>Always connected</h3>
                    <p>
                      Refreshes every minute while open. New epics appear
                      automatically. Update work in Azure DevOps and the board
                      follows.
                    </p>
                    <details>
                      <summary>How progress is counted</summary>
                      <p>
                        Completion counts Resolved + Closed work items.
                        Stories and bugs count once; their child tasks are shown
                        in detail but not counted again. Direct tasks count
                        separately. Removed work is excluded. Features with no
                        stories have no percentage. No effort weighting or
                        delivery forecast is inferred.
                      </p>
                      {!epic && (
                        <p>
                          {unparented} scoped work items have no visible epic
                          ancestor and are outside the portfolio totals.
                        </p>
                      )}
                    </details>
                  </section>
                </aside>
              </div>
            </>
          )
        )}
        <footer className="board-footer">
          <span>
            MARQUE <b>/</b> DELIVERY BOARDS
          </span>
          <span>
            {board.data
              ? `Verified ${new Date(board.data.asOf).toLocaleString("en-GB")} · Auto-refresh 60s`
              : "Waiting for progress data"}
          </span>
          <span>Internal · Sign-in required to share</span>
        </footer>
      </main>
    </div>
  );
}
